// A list's row density, fitted to the cell it is drawn in (TOM-395). The count
// picks a starting tier, but in the height-locked watch bento the cell's real
// height decides: a tier whose rows overflow the cell steps to the next tighter
// one, so a five-tank card at 1440×900 draws every row instead of cutting the
// last in half (ux_principles.md, "honest fit"). Outside the bento the page
// scrolls and the count's tier stands.

import { useLayoutEffect, useState, type RefObject } from 'react';

export type RowDensity = 'roomy' | 'normal' | 'tight';

/** The next tighter tier, or null at the tightest. */
export function tighterDensity(d: RowDensity): RowDensity | null {
  return d === 'roomy' ? 'normal' : d === 'normal' ? 'tight' : null;
}

/** True when a box's content is taller than the box: its rows are clipped. */
export function overflows(box: { scrollHeight: number; clientHeight: number }): boolean {
  return box.scrollHeight > box.clientHeight + 1;
}

export function useFittedDensity(
  list: RefObject<HTMLElement | null>,
  fromCount: RowDensity
): RowDensity {
  // `cell` is the list's height when the tier was last fitted, so a new size
  // re-runs the fit even when it starts again from the same tier.
  const [fitted, setFitted] = useState<{ from: RowDensity; density: RowDensity; cell: number }>({
    from: fromCount,
    density: fromCount,
    cell: 0,
  });
  // A new count starts again from its own tier.
  const density = fitted.from === fromCount ? fitted.density : fromCount;

  useLayoutEffect(() => {
    const el = list.current;
    if (el === null) return;
    if (!document.body.classList.contains('nm-bento') || !overflows(el)) return;
    const next = tighterDensity(density);
    if (next !== null) setFitted((f) => ({ ...f, from: fromCount, density: next }));
  }, [list, fromCount, density, fitted.cell]);

  // The cell changes size (a window resize, the bento engaging or letting go,
  // which happens after this list first lays out): start again from the
  // count's tier, and the effect above steps it down again.
  useLayoutEffect(() => {
    const el = list.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const cell = el.clientHeight;
      setFitted((f) => (f.cell === cell ? f : { from: fromCount, density: fromCount, cell }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [list, fromCount]);

  return density;
}
